import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { AssetPreferenceName } from '@infinex/asset-config';
import type {
  UpdateUserAssetPreferencesOutput,
  GetUserAssetPreferencesOutput,
  UpdateUserAssetPreferencesInput,
} from '@infinex/main-service-contract/v2/client/assets';

import { getTRPCClient } from '../config';
/**
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates#via-the-cache
 */
export function useOptimisticUserAssetPreferenceUpdate({
  preferenceName = 'is_hidden',
  onSuccess,
}: {
  preferenceName: AssetPreferenceName;
  onSuccess?: (
    data: UpdateUserAssetPreferencesOutput,
    variables: UpdateUserAssetPreferencesInput,
    context:
      | {
          previousPreferences: GetUserAssetPreferencesOutput | undefined;
        }
      | undefined
  ) => void;
}) {
  const queryClient = useQueryClient();

  const assetPreferencesQueryKey =
    getTRPCClient().assets.getUserAssetPreferences.queryKey({ preferenceName });

  return useMutation({
    ...getTRPCClient().assets.updateUserAssetPreferences.mutationOptions(),
    // When mutate is called:
    onMutate: async (preferenceUpdates) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: assetPreferencesQueryKey,
      });

      // Snapshot the previous value
      const snapshot = queryClient.getQueryData<GetUserAssetPreferencesOutput>(
        assetPreferencesQueryKey
      );

      // Optimistically update to the new value
      queryClient.setQueryData<GetUserAssetPreferencesOutput>(
        assetPreferencesQueryKey,
        (prev) => [
          ...(prev
            ? prev.filter(
                (existing) =>
                  !preferenceUpdates.updates.find(
                    (update) =>
                      update.address === existing.address &&
                      update.chain === existing.chain
                  )
              )
            : []),
          ...preferenceUpdates.updates.map((update, i) => ({
            ...update,
            id: String(i),
          })),
        ]
      );

      // Return a context object with the snapshotted value
      return { previousPreferences: snapshot };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, newTodo, context) => {
      queryClient.setQueryData(
        assetPreferencesQueryKey,
        context?.previousPreferences
      );
    },
    // Always refetch after error or success:
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: assetPreferencesQueryKey,
      }),
    onSuccess: (data, variables, context) => {
      onSuccess?.(data, variables, context);
    },
  });
}
